import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFormikContext} from 'formik';
import {useModalPopup} from '../../../../common/components/core/useModalPopup';
import {OrderInvoiceFormValues} from '../../types';
import {RivhitCustomer} from '../../../../common/types';
import {useOrderInvoiceFormContext} from '../OrderInvoiceFormContext';
import {shallowEqual, useSelector} from 'react-redux';
import {RootState} from '../../../../store/configureStore';

export type SelectCustomerPopupContext = ReturnType<typeof useModalPopup> & {
  select: (customer: RivhitCustomer) => void,
  loading: boolean,
  customers: RivhitCustomer[]
}

export interface SelectCustomerPopupContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext: SelectCustomerPopupContext = {
  open: false,
  openPopup: () => {},
  closePopup: () => {},
  select: () => {},
  loading: false,
  customers: []
};

export const SelectCustomerPopupContext = createContext<SelectCustomerPopupContext>(defaultContext);
SelectCustomerPopupContext.displayName = 'SelectCustomerPopupContext';

export const useSelectCustomerPopupContext = () => useContext(SelectCustomerPopupContext);

export const SelectCustomerPopupContextProvider = ({children}: SelectCustomerPopupContextProviderProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [customers, setCustomers] = useState<RivhitCustomer[]>([]);
  const {searchCustomers} = useOrderInvoiceFormContext();

  const userAgentID = useSelector(
    (state: RootState) => state.auth.user?.agentId,
    shallowEqual
  );
  const {setFieldValue} = useFormikContext<OrderInvoiceFormValues>();
  const {open, openPopup, closePopup} = useModalPopup();

  useEffect(() => {
    setLoading(true);
    update()
      .finally(() => setLoading(false));
  }, [userAgentID]);

  const update = async () => {
    const list = await searchCustomers();
    setCustomers(list);
  };

  const select = (customer: RivhitCustomer) => {
    setFieldValue('customerName', customer.customerName);
    setFieldValue('idNumber', customer.idNumber);
    setFieldValue('address', customer.address);
    setFieldValue('phoneFax', customer.phoneFax);
    setFieldValue('contact', customer.contact);
    setFieldValue('rivhitCustomerID', customer.rivhitCustomerID);
    setFieldValue('emails', customer.emails);
  };

  const context: SelectCustomerPopupContext = {
    ...defaultContext,
    open,
    openPopup, closePopup,
    select,
    loading,
    customers,
  };

  return (
    <SelectCustomerPopupContext.Provider value={context}>
      {children}
    </SelectCustomerPopupContext.Provider>
  );
};
